import { Injectable, computed, inject, signal } from '@angular/core';
import { Store } from '@ngrx/store';

import { selectUser } from '@core/store/auth/auth.selectors';

import { LocalStorageUtil } from '@shared/utils/storage.utils';

/**
 * The last dataspace the user picked, whether in an admin page's dropdown or by visiting a
 * `/ds/:id` route, so admin pages and the Data/Plans links all open on the same dataspace.
 * Falls back to the user's default dataspace until one is picked.
 */
@Injectable({ providedIn: 'root' })
export class DataspaceSelectionService {
  private static readonly STORAGE_KEY = 'dataspace.lastSelectedId';

  private readonly user = inject(Store).selectSignal(selectUser);

  // `undefined` means nothing was picked yet, so the default applies; `null` is an explicit
  // clear, which must not snap back to the default while the user is still on the page.
  private readonly picked = signal<number | null | undefined>(
    LocalStorageUtil.getItem<number>(DataspaceSelectionService.STORAGE_KEY) ?? undefined
  );

  readonly selectedId = computed<number | null>(() => {
    const picked = this.picked();
    return picked !== undefined ? picked : (this.user()?.defaultDataSpace?.id ?? null);
  });

  select(id: number | null): void {
    this.picked.set(id);
    if (id == null) LocalStorageUtil.removeItem(DataspaceSelectionService.STORAGE_KEY);
    else LocalStorageUtil.setItem(DataspaceSelectionService.STORAGE_KEY, id);
  }

  /**
   * Drops `id` if it is the stored pick, so a dataspace that can no longer be opened (deleted,
   * or access revoked) stops being offered and the user's default applies again.
   */
  forget(id: number): void {
    if (this.picked() !== id) return;
    this.picked.set(undefined);
    LocalStorageUtil.removeItem(DataspaceSelectionService.STORAGE_KEY);
  }
}
